import { useEffect, useState, useCallback } from 'react';

import { InboxOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Drawer, Form, Input, Modal, Select, Space, Upload, message } from 'antd';
import { UploadFile, RcFile } from 'antd/lib/upload';

import { useAuth } from '@/hooks/useAuth';
import { useDisclosure } from '@/hooks/useDisclosure';

import { ServiceRequestDrawerProps } from './serviceTypes';

const { Dragger } = Upload;

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/zip',
  'application/x-rar-compressed',
];

const PRIORITY_OPTIONS = [
  { value: 'alto', label: 'Alta' },
  { value: 'medio', label: 'Média' },
  { value: 'baixo', label: 'Baixa' },
];

interface FormValues {
  message: string;
  priority: 'alto' | 'medio' | 'baixo';
  emails?: string[];
  files?: UploadFile[];
}

export const ServiceRequestDrawer = ({
  isOpen,
  onClose,
  service,
  onSubmit,
}: ServiceRequestDrawerProps) => {
  const [form] = Form.useForm<FormValues>();
  const { getAuthUser } = useAuth();
  const [userEmail, setUserEmail] = useState<string>('');
  const modal = useDisclosure();
  const [messageApi] = message.useMessage();
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    const user = getAuthUser();
    if (user?.email) {
      setUserEmail(user.email);
      const initialEmails = [user.email];
      form.setFieldValue('emails', initialEmails);
    }
  }, [form, getAuthUser]);

  const handleEmailDeselect = useCallback(
    (value: string) => {
      if (value === userEmail) {
        setTimeout(() => {
          messageApi.warning('Não é possível remover o seu email');
        }, 0);
        return;
      }
      setAdditionalEmails((prev) => prev.filter((email) => email !== value));
    },
    [userEmail, messageApi],
  );

  const beforeUpload = (file: RcFile) => {
    if (file.size > MAX_FILE_SIZE) {
      messageApi.error(`O arquivo ${file.name} é muito grande. Tamanho máximo: 50MB`);
      return false;
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      messageApi.warning(`Tipo de arquivo ${file.type} pode não ser suportado pelo Zendesk`);
    }

    return false;
  };

  const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
    form.setFieldValue('files', newFileList);
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);

      if (!values.message?.trim()) {
        throw new Error('A mensagem não pode estar vazia');
      }

      if (!values.priority) {
        throw new Error('A prioridade é obrigatória');
      }

      const files: File[] = fileList.map((file) => file.originFileObj as File).filter(Boolean);

      const formData = {
        service,
        message: values.message,
        priority: values.priority,
        to: [],
        emails: values.emails || [],
        files,
      };

      await onSubmit(service, values.message, values.priority, formData);

      messageApi.success('Solicitação enviada com sucesso!');
      form.resetFields();
      setFileList([]);
      onClose();
    } catch (error) {
      if (error instanceof Error && error.message.includes('ERR_FAILED') && error.message.includes('201')) {
        messageApi.success('Solicitação enviada com sucesso!');
        form.resetFields();
        setFileList([]);
        onClose();
      } else {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Erro ao enviar solicitação. Por favor, tente novamente.';
        messageApi.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateEmails = (_: unknown, value: string[]): Promise<void> => {
    if (!value?.length) {
      return Promise.reject(new Error('Pelo menos um email é necessário'));
    }

    if (!value.includes(userEmail)) {
      return Promise.reject(new Error('O seu email é obrigatório'));
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = value.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      return Promise.reject(new Error('Um ou mais emails são inválidos'));
    }

    return Promise.resolve();
  };

  const allEmails = [userEmail, ...additionalEmails].filter(Boolean);

  const handleEmailChange = (values: string[]) => {
    const requiredEmails = [userEmail];
    const missingRequired = requiredEmails.filter((email) => !values.includes(email));
    let updatedValues = values;

    if (missingRequired.length > 0) {
      updatedValues = [...values, ...missingRequired];
    }

    const additionals = updatedValues.filter((email) => email !== userEmail);
    setAdditionalEmails(additionals);
  };

  return (
    <>
      <Drawer
        title={`Solicitar ${service.item}`}
        placement="right"
        width={500}
        onClose={onClose}
        open={isOpen}
        extra={
          <Space>
            <Button data-testid="closeServiceDrawer" onClick={modal.open}>
              Cancelar
            </Button>
            <Button
              type="primary"
              data-testid="submitServiceDrawer"
              htmlType="submit"
              form="serviceRequestForm"
              loading={isSubmitting}
            >
              Enviar Solicitação
            </Button>
          </Space>
        }
      >
        <Form
          id="serviceRequestForm"
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          initialValues={{ priority: 'medio' }}
        >
          <Space direction="vertical" className="u-w-full">
            <Form.Item
              name="message"
              label="Mensagem"
              rules={[{ required: true, message: 'Por favor, insira uma mensagem' }]}
            >
              <Input.TextArea rows={6} placeholder="Descreva sua solicitação..." />
            </Form.Item>

            <Form.Item
              name="priority"
              label="Prioridade"
              rules={[{ required: true, message: 'Por favor, selecione a prioridade' }]}
            >
              <Select placeholder="Selecione a prioridade" options={PRIORITY_OPTIONS} />
            </Form.Item>

            <Form.Item
              name="emails"
              label="Emails para contato"
              rules={[
                { required: true, message: 'Por favor, informe pelo menos um email' },
                { validator: validateEmails },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Adicione emails para contato"
                suffixIcon={<MailOutlined />}
                value={allEmails}
                onChange={handleEmailChange}
                onDeselect={handleEmailDeselect}
                maxTagCount="responsive"
                style={{ width: '100%' }}
              >
                <Select.Option value={userEmail} disabled>
                  {userEmail} (seu email)
                </Select.Option>
                {additionalEmails.map((email) => (
                  <Select.Option key={email} value={email}>
                    {email}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="files" label="Anexar arquivos (opcional)">
              <Dragger
                multiple
                fileList={fileList}
                beforeUpload={beforeUpload}
                onChange={handleFileChange}
                onRemove={(file) => {
                  const newFileList = fileList.filter((item) => item.uid !== file.uid);
                  setFileList(newFileList);
                  form.setFieldValue('files', newFileList);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Clique ou arraste arquivos para esta área</p>
                <p className="ant-upload-hint">
                  Upload de múltiplos arquivos (máximo 50MB por arquivo)
                </p>
              </Dragger>
            </Form.Item>
          </Space>
        </Form>
      </Drawer>

      <Modal
        title="Deseja realmente cancelar a solicitação?"
        open={modal.isOpen}
        okButtonProps={{ danger: true }}
        okText="Cancelar"
        cancelText="Voltar"
        onOk={() => {
          modal.close();
          onClose();
          form.resetFields();
        }}
        onCancel={modal.close}
      >
        <p>
          Todas as informações inseridas para solicitação serão perdidas e não poderão ser
          recuperadas futuramente.
        </p>
      </Modal>
    </>
  );
};
